import { ColorPicker, ICalloutContentStyles, getTheme } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPActionButton, IPLabel, IPPrimaryButton, IPSpinner, IPText } from 'baseComponents';
import { IPCallout } from 'baseComponents/Callout/IPCallout';
import { IPDropdown } from 'baseComponents/Dropdown/IPDropdown';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPSelectableOption } from 'baseComponents/Utils/SelectableOption';
import PeriodFilter, { IPeriodFilterData, PeriodFilterDataOption } from 'components/Filter/PeriodFilter';
import { getPeriodFilterEndDate, getPeriodFilterStartDate } from 'components/Filter/PeriodFilterHelper';
import KPIDataContextFilter from 'components/KPI/Data/KPIDataContextFilter';
import { getAggregationMethodOptions } from 'components/KPI/KPIHelpers';
import HrefLink from 'components/Links/HrefLink';
import TextInfo from 'components/Notification/Info';
import EntityPicker from 'components/Pickers/EntityPicker';
import { NumericField } from 'components/Text/NumericField';
import { globalStackTokensGapMedium, globalStackTokensGapSmall, switchIcon } from 'globalStyles';
import { mapToKPIContextFilter } from 'mappings/kpiMapping';
import { DashboardTypes } from 'models/dashboard';
import { KPIGraphDataConfigDTO } from 'models/dto/kpi/kpiGraphDataDTO';
import Entity, { EntityTypes } from 'models/entity';
import KPI, { KPIAggregationMethods, KPITypes } from 'models/kpi/kpi';
import { KPIAlertPeriods } from 'models/kpi/KPIAlert';
import { KPIContextFilter } from 'models/kpi/kpiDataContext';
import { FormEvent, Fragment, FunctionComponent, useContext, useEffect, useState } from 'react';
import ReactSpeedometer, { Transition } from 'react-d3-speedometer';
import { useTranslation } from 'react-i18next';
import { apiGetWidgetDataSingleKPILastPeriod } from 'services/Api/dashboardService';
import { apiGetKPI, apiGetKPIs } from 'services/Api/kpiService';
import { apiRequest } from 'services/Auth/authConfig';
import { resolveHexColor } from 'utils/colorHelpers';
import { fromApiDateOptional, toApiDateTimeOptional } from 'utils/datetime';
import { IWidgetConfigRendererProps } from '../WidgetConfigRenderer';
import { IWidgetRendererProps } from '../WidgetRenderer';

export class WidgetSingleKPILastPeriodConfig {
  kpiId: number;

  aggregationMethod: KPIAggregationMethods;

  aggregationPeriod: KPIAlertPeriods;

  period?: IPeriodFilterData;

  rawFilter: string;

  filter: KPIContextFilter;

  minValue: number;

  maxValue: number;

  tresholdHigh: number | undefined;

  tresholdLow: number | undefined;

  tresholdHighColor: string | undefined;

  tresholdMediumColor: string | undefined;

  tresholdLowColor: string | undefined;

  constructor() {
    this.kpiId = 0;
    this.aggregationMethod = KPIAggregationMethods.Count;
    this.aggregationPeriod = KPIAlertPeriods.Month;
    this.tresholdHigh = undefined;
    this.tresholdLow = undefined;
    this.tresholdHighColor = undefined;
    this.tresholdMediumColor = undefined;
    this.tresholdLowColor = undefined;
    this.rawFilter = '';
    this.filter = new KPIContextFilter();
    this.minValue = 0;
    this.maxValue = 100;
  }

  load(raw: string | undefined) {
    if (raw) {
      try {
        const newRawConfig = JSON.parse(raw);
        this.kpiId = newRawConfig.kpiId ?? 0;
        this.aggregationMethod = newRawConfig.aggregationMethod ?? KPIAggregationMethods.Count;
        this.aggregationPeriod = newRawConfig.aggregationPeriod ?? KPIAlertPeriods.Month; //deprecated, will be removed in favor of period
        this.tresholdHigh = newRawConfig.tresholdHigh;
        this.tresholdLow = newRawConfig.tresholdLow;
        this.tresholdHighColor = newRawConfig.tresholdHighColor;
        this.tresholdMediumColor = newRawConfig.tresholdMediumColor;
        this.tresholdLowColor = newRawConfig.tresholdLowColor;
        this.rawFilter = newRawConfig.rawFilter;
        this.minValue = newRawConfig.minValue ?? 0;
        this.maxValue = newRawConfig.maxValue ?? 100;
        if (this.rawFilter) {
          this.filter = KPIContextFilter.fromJSON(this.rawFilter);
        }
        this.period = newRawConfig.period;
        //backward compatibility - if period is not defined, try to create it from periodStart and periodEnd
        if (!this.period) {
          switch (this.aggregationPeriod) {
            case KPIAlertPeriods.Week:
              this.period = {
                option: PeriodFilterDataOption.LastWeek,
              };
              break;
            case KPIAlertPeriods.Month:
              this.period = {
                option: PeriodFilterDataOption.LastMonth,
              };
              break;
            case KPIAlertPeriods.Quarter:
              this.period = {
                option: PeriodFilterDataOption.LastQuarter,
              };
              break;
            case KPIAlertPeriods.Year:
              this.period = {
                option: PeriodFilterDataOption.LastYear,
              };
              break;
          }
        }
      } catch {
        //ignore
      }
    }
  }

  clone(): WidgetSingleKPILastPeriodConfig {
    const newConfig = new WidgetSingleKPILastPeriodConfig();
    newConfig.kpiId = this.kpiId;
    newConfig.aggregationMethod = this.aggregationMethod;
    newConfig.aggregationPeriod = this.aggregationPeriod;
    newConfig.tresholdHigh = this.tresholdHigh;
    newConfig.tresholdLow = this.tresholdLow;
    newConfig.tresholdHighColor = this.tresholdHighColor;
    newConfig.tresholdMediumColor = this.tresholdMediumColor;
    newConfig.tresholdLowColor = this.tresholdLowColor;
    newConfig.filter = this.filter;
    newConfig.rawFilter = this.rawFilter;
    newConfig.minValue = this.minValue;
    newConfig.maxValue = this.maxValue;
    newConfig.period = this.period;

    return newConfig;
  }
}

//segment color config
const defColorTresholdHigh = '#D14222';
const defColorTresholdMedium = '#E1D045';
const defColorTresholdLow = '#3DAA47';

const getSegmentColors = (config: WidgetSingleKPILastPeriodConfig): string[] | undefined => {
  if (getSegmentStops(config) !== undefined) {
    const colorLow = resolveHexColor(config.tresholdLowColor, defColorTresholdLow);
    const colorMedium = resolveHexColor(config.tresholdMediumColor, defColorTresholdMedium);
    const colorHigh = resolveHexColor(config.tresholdHighColor, defColorTresholdHigh);

    return [colorLow, colorMedium, colorHigh];
  }

  return [resolveHexColor(config.tresholdLowColor, defColorTresholdLow)];
};

//segment stops config
const getSegmentStops = (config: WidgetSingleKPILastPeriodConfig): number[] | undefined => {
  if (config.tresholdHigh !== undefined && config.tresholdLow !== undefined) {
    return [config.minValue, config.tresholdLow, config.tresholdHigh, config.maxValue];
  }

  return undefined;
};

//value format
const getValueFormat = (kpi: KPI, aggregationMethod: KPIAggregationMethods): string => {
  if (kpi.type === KPITypes.SuccesError && aggregationMethod === KPIAggregationMethods.Avg) {
    return `.0%`;
  }

  return `.${kpi.decimalCount ?? 0}f`;
};

interface IWidgetSingleKPILastPeriodProps extends IWidgetRendererProps {}

const WidgetSingleKPILastPeriod: FunctionComponent<IWidgetSingleKPILastPeriodProps> = (
  props: IWidgetSingleKPILastPeriodProps,
) => {
  const { t } = useTranslation(['translation', 'widgets']);
  const appContext = useContext(AppContext);
  const [kpi, setKPI] = useState<KPI | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [config, setConfig] = useState<WidgetSingleKPILastPeriodConfig | undefined>(undefined);
  const theme = getTheme();

  useEffect(() => {
    const loadConfig = (): WidgetSingleKPILastPeriodConfig => {
      const newConfig = new WidgetSingleKPILastPeriodConfig();
      newConfig.load(props.widget.widgetConfig);

      if (props.period) {
        newConfig.period = props.period; //override period with the one from props
      }

      return newConfig;
    };

    const loadData = async () => {
      try {
        if (isLoading) return;
        setIsLoading(true);
        setKPI(undefined);
        setConfig(undefined);

        const config = loadConfig();
        setConfig(config);

        if (config && config.kpiId) {
          const accessToken = await appContext.getAccessToken(apiRequest.scopes);
          const kpi = await apiGetKPI(config.kpiId, accessToken, undefined);

          const graphConfig = mapToKPIGraphDataConfigFromSingleKPIWidgetConfig(config);
          const data = await apiGetWidgetDataSingleKPILastPeriod(graphConfig, accessToken, props.abortSignal);

          if (kpi) kpi.graphData = data;
          setKPI(kpi);
        }
      } catch (err) {
        appContext.setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget]);

  //mapping function for back-end
  const mapToKPIGraphDataConfigFromSingleKPIWidgetConfig = (
    config: WidgetSingleKPILastPeriodConfig,
  ): KPIGraphDataConfigDTO => {
    const output = new KPIGraphDataConfigDTO();

    output.kpiId = config.kpiId;
    output.aggregationPeriod = config.aggregationPeriod;
    output.aggregationMethod = config.aggregationMethod;
    output.contextFilter = mapToKPIContextFilter(config.filter);

    //calculate start and end dates
    const customStart = fromApiDateOptional(config.period?.customStart);
    const customEnd = fromApiDateOptional(config.period?.customEnd);
    const start = getPeriodFilterStartDate(config.period?.option, config.period?.auditYear, customStart, appContext);
    const end = getPeriodFilterEndDate(config.period?.option, config.period?.auditYear, customEnd, appContext);

    output.periodStart = toApiDateTimeOptional(start);
    output.periodEnd = toApiDateTimeOptional(end);

    return output;
  };

  //
  // Main render
  //
  if (isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  if (!kpi || !kpi.graphData || !kpi.graphData.points.length) {
    //config is invalid
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPText>{t('widgets:SingleKPILastPeriod.Config.Invalid')}</IPText>
      </IPStack>
    );
  }

  const textColor = appContext.useDarkMode ? theme.palette.white : theme.palette.black;

  return (
    <IPStack verticalFill tokens={globalStackTokensGapSmall} horizontalAlign="center">
      <IPStack.Item styles={{ root: { paddingTop: 15 } }}>
        <ReactSpeedometer
          segments={config ? (getSegmentStops(config) === undefined ? 1 : 3) : undefined}
          minValue={config?.minValue}
          maxValue={config?.maxValue}
          value={kpi.graphData?.points[0].value}
          width={300}
          height={200}
          needleColor={'#3366ff'}
          segmentColors={config ? getSegmentColors(config) : undefined}
          ringWidth={25}
          valueTextFontSize="18"
          valueTextFontWeight="500"
          needleHeightRatio={0.8}
          textColor={textColor}
          needleTransitionDuration={500}
          needleTransition={Transition.easeBackOut}
          valueFormat={getValueFormat(kpi, config?.aggregationMethod ?? KPIAggregationMethods.Count)}
          customSegmentStops={config ? getSegmentStops(config) : undefined}
        />
      </IPStack.Item>
      <HrefLink href={`/kpi/${kpi.kpiId}/data`}>{kpi.name}</HrefLink>
    </IPStack>
  );
};

export default WidgetSingleKPILastPeriod;

//
// Config
//

interface IWidgetConfigSingleKPILastPeriodProps extends IWidgetConfigRendererProps {}

export const WidgetConfigSingleKPILastPeriod: FunctionComponent<IWidgetConfigSingleKPILastPeriodProps> = (
  props: IWidgetConfigSingleKPILastPeriodProps,
) => {
  const { t } = useTranslation(['kpi', 'kpis', 'translation', 'widgets']);
  const appContext = useContext(AppContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [kpis, setKPIs] = useState<KPI[]>([]);
  const [kpiEntities, setKPIEntities] = useState<Entity[]>([]);
  const [selectedKPI, setSelectedKPI] = useState<KPI | undefined>(undefined);
  const [selectedEntity, setSelectedEntity] = useState<Entity | undefined>(undefined);
  const [colorCallout, setColorCallouth] = useState<number>(0);
  const [currentWidgetId, setCurrentWidgetId] = useState<number>(-1);
  const [colorTarget, setColorTarget] = useState<string>('dummy');
  const theme = getTheme();

  const loadConfig = (): WidgetSingleKPILastPeriodConfig => {
    const newConfig = new WidgetSingleKPILastPeriodConfig();
    newConfig.load(props.widget.widgetConfig);

    if (props.period) {
      newConfig.period = props.period; //override period with the one from props
    }

    return newConfig;
  };

  const [config, setConfig] = useState<WidgetSingleKPILastPeriodConfig>(loadConfig());

  useEffect(() => {
    if (currentWidgetId === -1 || currentWidgetId !== props.widget.widgetId) {
      setCurrentWidgetId(props.widget.widgetId);
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget]);

  const loadData = async () => {
    try {
      if (isLoading || currentWidgetId === props.widget.widgetId) return;
      setIsLoading(true);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      let kpis = await apiGetKPIs(accessToken, undefined);
      kpis = kpis.filter((k) => k.isCalcType());
      setKPIs(kpis);

      const entities = getEntitiesFromKPIs(kpis);
      setKPIEntities(entities);

      if (config.kpiId) {
        const entity = entities.find((e) => e.entityId === config.kpiId);
        setSelectedEntity(entity);
        setSelectedKPI(kpis.find((k) => k.kpiId === config.kpiId));
      }

      //setSelectedPeriod(config.aggregationPeriod);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const getEntitiesFromKPIs = (kpis: KPI[]): Entity[] => {
    const entities: Entity[] = [];

    entities.push(
      ...kpis.map((c) => {
        const newEntity = new Entity();
        newEntity.entityId = c.kpiId;
        newEntity.typeOfEntity = EntityTypes.KPI;
        newEntity.entityName = c.name;

        return newEntity;
      }),
    );

    return entities;
  };

  const isConfigValid = (config: WidgetSingleKPILastPeriodConfig): boolean => {
    //kpi must be selected
    if (config.kpiId <= 0) return false;

    return true;
  };

  const updateConfig = (config: WidgetSingleKPILastPeriodConfig) => {
    const newConfig = config.clone();
    newConfig.rawFilter = JSON.stringify(newConfig.filter);
    setConfig(newConfig);
    props.onUpdateConfig(JSON.stringify(newConfig), isConfigValid(newConfig));
  };

  const onAddSelectedEntity = (entity: Entity) => {
    setSelectedEntity(entity);
    config.kpiId = entity.entityId;

    const kpi = kpis.find((k) => k.kpiId === entity.entityId);
    setSelectedKPI(kpi);

    //after selecting a KPI, initialize the min and max values if they are set on the KPI
    if (kpi?.minValue) config.minValue = Number.parseFloat(kpi.minValue);
    if (kpi?.maxValue) config.maxValue = Number.parseFloat(kpi.maxValue);

    updateConfig(config);
  };

  const onRemoveSelectedEntity = () => {
    setSelectedEntity(undefined);
    config.kpiId = 0;
    updateConfig(config);
  };

  const calloutStyle: Partial<ICalloutContentStyles> = {
    root: {
      padding: '10px 12px',
      maxWidth: 600,
    },
  };

  const onSetPeriod = (period: IPeriodFilterData | undefined) => {
    config.period = period;
    updateConfig(config);
  };

  const getColorToEdit = (): string => {
    switch (colorCallout) {
      case 1:
        return resolveHexColor(config.tresholdLowColor, defColorTresholdLow);
      case 2:
        return resolveHexColor(config.tresholdMediumColor, defColorTresholdMedium);
      case 3:
        return resolveHexColor(config.tresholdHighColor, defColorTresholdHigh);
    }

    return defColorTresholdHigh;
  };

  const onSwapTresholdColors = () => {
    const newConfig = config.clone();
    const colorLow = resolveHexColor(newConfig.tresholdLowColor, defColorTresholdLow);
    const colorHigh = resolveHexColor(newConfig.tresholdHighColor, defColorTresholdHigh);

    newConfig.tresholdLowColor = colorHigh;
    newConfig.tresholdHighColor = colorLow;

    updateConfig(newConfig);
  };

  //
  // Main render
  //
  return (
    <IPStack verticalFill tokens={globalStackTokensGapSmall}>
      <IPStack.Item>
        <IPLabel>{t('widgets:SingleKPILastPeriod.Config.KPI.Label')}</IPLabel>
        <EntityPicker
          entities={kpiEntities}
          isLoading={isLoading}
          addSelectedEntity={onAddSelectedEntity}
          clearSearchText={onRemoveSelectedEntity}
          loadData={loadData}
          setSearchTextToSelectedEntity={true}
          selectedEntity={selectedEntity}
          isOnPanel={true}
        ></EntityPicker>
      </IPStack.Item>
      {selectedEntity && selectedKPI && (
        <Fragment>
          <IPStack.Item>
            <IPDropdown
              label={t('widgets:SingleKPILastPeriod.Config.AggregationMethod.Label')}
              selectedKey={config.aggregationMethod}
              options={getAggregationMethodOptions(t)}
              styles={{ listbox: { maxHeight: 250 } }}
              onChange={(event: FormEvent<HTMLDivElement>, option?: IPSelectableOption, index?: number) => {
                config.aggregationMethod = option?.key as KPIAggregationMethods;
                updateConfig(config);
              }}
            />
          </IPStack.Item>
          <IPStack.Item>
            <IPStack horizontal tokens={globalStackTokensGapMedium} verticalAlign="end">
              <IPStack.Item>
                <PeriodFilter
                  showOption={true}
                  showCurrentAuditYear={true}
                  storageKey={undefined}
                  onUpdatePeriodOption={onSetPeriod}
                  period={config.period}
                  showAll={true}
                  disabled={
                    props.widget.dashboard?.dashboardType === DashboardTypes.control &&
                    props.widget.dashboard?.isContextType()
                  }
                />
              </IPStack.Item>
              <IPStack.Item>
                <KPIDataContextFilter
                  kpi={selectedKPI}
                  filter={config.filter}
                  onUpdate={(newFilter) => {
                    const newConfig = config.clone();
                    newConfig.filter = newFilter;
                    updateConfig(newConfig);
                  }}
                />
              </IPStack.Item>
            </IPStack>
          </IPStack.Item>
          <IPStack.Item>
            <TextInfo message={t('widgets:SingleKPILastPeriod.Config.Meter.Info')}>
              <IPLabel>{t('widgets:SingleKPILastPeriod.Config.Meter.Label')}</IPLabel>
            </TextInfo>
            <IPStack
              horizontal
              verticalAlign="center"
              tokens={globalStackTokensGapMedium}
              horizontalAlign="space-between"
              styles={{
                root: {
                  padding: 10,
                  background: appContext.useDarkMode ? theme.palette.themeDarker : theme.palette.themeLighter,
                },
              }}
            >
              <IPStack.Item styles={{ root: { width: '28%' } }}>
                <IPStack tokens={globalStackTokensGapSmall}>
                  <IPStack.Item>
                    <NumericField
                      label={t('widgets:SingleKPILastPeriod.Config.Meter.TresholdLow')}
                      value={config.tresholdLow}
                      minValue={config.minValue}
                      maxValue={config.tresholdHigh ?? config.maxValue}
                      decimalCount={selectedKPI.decimalCount}
                      allowEmpty={true}
                      onChange={(val) => {
                        const newConfig = config.clone();
                        newConfig.tresholdLow = val;
                        if (newConfig.tresholdHigh === undefined) newConfig.tresholdHigh = val;
                        updateConfig(newConfig);
                      }}
                    />
                  </IPStack.Item>
                  <IPStack.Item>
                    <IPActionButton
                      id="color_callout_1"
                      iconProps={{ iconName: 'Color' }}
                      text={t('widgets:SingleKPILastPeriod.Config.Meter.TresholdColorLow')}
                      onClick={() => {
                        setColorTarget('color_callout_1');
                        setColorCallouth(1);
                      }}
                    />
                  </IPStack.Item>
                  <IPStack.Item>
                    <NumericField
                      label={t('widgets:SingleKPILastPeriod.Config.Meter.MinValue')}
                      value={config.minValue}
                      minValue={selectedKPI.minValue ? Number.parseFloat(selectedKPI.minValue ?? '0') : undefined}
                      maxValue={config.tresholdLow ?? config.tresholdHigh ?? config.maxValue}
                      decimalCount={selectedKPI.decimalCount}
                      allowEmpty={false}
                      onChange={(val) => {
                        const newConfig = config.clone();
                        newConfig.minValue = val ?? 0;
                        updateConfig(newConfig);
                      }}
                    />
                  </IPStack.Item>
                </IPStack>
              </IPStack.Item>
              <IPStack.Item styles={{ root: { width: '35%' } }}>
                <IPStack tokens={globalStackTokensGapSmall} horizontalAlign="center">
                  <IPStack.Item>
                    <IPActionButton
                      id="color_callout_2"
                      iconProps={{ iconName: 'Color' }}
                      text={t('widgets:SingleKPILastPeriod.Config.Meter.TresholdColorMedium')}
                      onClick={() => {
                        setColorTarget('color_callout_2');
                        setColorCallouth(2);
                      }}
                    />
                  </IPStack.Item>
                  <IPStack.Item>
                    <IPActionButton
                      iconProps={switchIcon}
                      text={t('widgets:SingleKPILastPeriod.Config.Meter.SwapColors')}
                      onClick={onSwapTresholdColors}
                    />
                  </IPStack.Item>
                  {!appContext.isMobileView && (
                    <IPStack.Item>
                      <ReactSpeedometer
                        forceRender={true}
                        segments={config ? (getSegmentStops(config) === undefined ? 1 : 3) : undefined}
                        minValue={config.minValue}
                        maxValue={config.maxValue}
                        value={(config.minValue + config.maxValue) / 2}
                        width={200}
                        height={130}
                        needleColor={'#3366ff'}
                        segmentColors={getSegmentColors(config)}
                        ringWidth={20}
                        valueTextFontSize="18"
                        valueTextFontWeight="500"
                        needleHeightRatio={0.7}
                        needleTransitionDuration={0}
                        needleTransition={Transition.easeBackOut}
                        valueFormat={getValueFormat(selectedKPI, config.aggregationMethod)}
                        customSegmentStops={config ? getSegmentStops(config) : undefined}
                      />
                    </IPStack.Item>
                  )}
                </IPStack>
              </IPStack.Item>
              <IPStack.Item styles={{ root: { width: '28%' } }}>
                <IPStack tokens={globalStackTokensGapSmall}>
                  <IPStack.Item>
                    <NumericField
                      label={t('widgets:SingleKPILastPeriod.Config.Meter.TresholdHigh')}
                      value={config.tresholdHigh}
                      minValue={config.tresholdLow ?? config.minValue}
                      maxValue={config.maxValue}
                      decimalCount={selectedKPI.decimalCount}
                      allowEmpty={true}
                      onChange={(val) => {
                        const newConfig = config.clone();
                        newConfig.tresholdHigh = val;
                        if (newConfig.tresholdLow === undefined) newConfig.tresholdLow = val;
                        updateConfig(newConfig);
                      }}
                    />
                  </IPStack.Item>
                  <IPStack.Item>
                    <IPActionButton
                      id="color_callout_3"
                      iconProps={{ iconName: 'Color' }}
                      text={t('widgets:SingleKPILastPeriod.Config.Meter.TresholdColorHigh')}
                      onClick={() => {
                        setColorTarget('color_callout_3');
                        setColorCallouth(3);
                      }}
                    />
                  </IPStack.Item>
                  <IPStack.Item>
                    <NumericField
                      label={t('widgets:SingleKPILastPeriod.Config.Meter.MaxValue')}
                      value={config.maxValue}
                      minValue={config.tresholdHigh ?? config.tresholdLow ?? config.minValue}
                      maxValue={selectedKPI.maxValue ? Number.parseFloat(selectedKPI.maxValue ?? '1000') : undefined}
                      decimalCount={selectedKPI.decimalCount}
                      allowEmpty={false}
                      onChange={(val) => {
                        const newConfig = config.clone();
                        newConfig.maxValue = val ?? 0;
                        updateConfig(newConfig);
                      }}
                    />
                  </IPStack.Item>
                </IPStack>
              </IPStack.Item>
            </IPStack>
          </IPStack.Item>
          <IPCallout
            styles={calloutStyle}
            hidden={!colorCallout}
            setInitialFocus
            gapSpace={0}
            target={`#${colorTarget}`}
            onDismiss={() => setColorCallouth(0)}
            dismissOnTargetClick
          >
            <ColorPicker
              color={getColorToEdit()}
              onChange={(ev, color) => {
                const newConfig = config.clone();
                switch (colorCallout) {
                  case 1:
                    newConfig.tresholdLowColor = '#' + color.hex;
                    break;
                  case 2:
                    newConfig.tresholdMediumColor = '#' + color.hex;
                    break;
                  case 3:
                    newConfig.tresholdHighColor = '#' + color.hex;
                    break;
                }
                updateConfig(newConfig);
              }}
              alphaType={'none'}
              showPreview
            />
            <IPPrimaryButton autoFocus onClick={() => setColorCallouth(0)}>
              {t('translation:General.Button.Close')}
            </IPPrimaryButton>
          </IPCallout>
        </Fragment>
      )}
    </IPStack>
  );
};
